/**
 * Document Type Registry — Single source of truth for all document type metadata.
 *
 * Feeds BOTH the Docu Type upload dropdown AND the Expected Documents checklist.
 * One entry per document type per entity. Pure data + pure functions, no React.
 *
 * Entity types: address, vehicle, insurance, health, financial, personal (future).
 *
 * Usage:
 *   getDocTypeLabels('vehicle')         → { registration: 'Registration', ... }
 *   getDocTypeColors('vehicle')         → { registration: 'bg-blue-100 text-blue-700', ... }
 *   getDocTypeOptions('vehicle')        → ['registration', 'title', ...]
 *   getDocTypePickerOptions('vehicle')  → [{ value: 'registration', label: 'Registration' }, ...]
 *   getExpectedDocuments('address', { ownership: 'owned' }) → ExpectedDocumentBlueprint[]
 */

import type { Address, AddressOwnership } from '../types/profile';

// ── Core Types ──────────────────────────────────────────────

export interface DocumentTypeEntry {
  /** Machine key matching the type union (e.g., 'deed', 'registration') */
  type: string;
  /** Human-readable label for dropdowns and detail sheets */
  label: string;
  /** Tailwind badge classes (e.g., 'bg-blue-100 text-blue-700') */
  color: string;
  /** Short description — feeds the Expected Documents checklist subtitle */
  description: string;
  /** Ownership types this doc type applies to (address only). Undefined = all. */
  appliesTo?: AddressOwnership[];
  /** When true, only shown for home/work address types (not doctor, school, etc.) */
  homeOnly?: boolean;
}

export type DocumentEntityType = 'address' | 'vehicle' | 'insurance' | 'health' | 'financial' | 'personal' | 'utility' | 'service-provider';



// ── Address ─────────────────────────────────────────────────

const ADDRESS_TYPES: DocumentTypeEntry[] = [
  { type: 'lease',       label: 'Lease',        color: 'bg-blue-100 text-blue-700', description: 'Signed rental agreement',       appliesTo: ['rented'], homeOnly: true },
  { type: 'deed',        label: 'Deed',         color: 'bg-blue-100 text-blue-700', description: 'Proof of property ownership',   appliesTo: ['owned'], homeOnly: true },
  { type: 'insurance',   label: 'Insurance',    color: 'bg-blue-100 text-blue-700', description: 'Insurance declaration page',    homeOnly: true },
  { type: 'maintenance', label: 'Maintenance',  color: 'bg-blue-100 text-blue-700', description: 'Warranty or maintenance records', homeOnly: true },
  { type: 'tax',         label: 'Property Tax', color: 'bg-blue-100 text-blue-700', description: 'Annual tax statement or receipt', appliesTo: ['owned'], homeOnly: true },
  { type: 'hoa',         label: 'HOA',          color: 'bg-blue-100 text-blue-700', description: 'CC&Rs, rules, or dues schedule',  appliesTo: ['owned'], homeOnly: true },
  { type: 'other',       label: 'Other',        color: 'bg-blue-100 text-blue-700', description: 'Other document' },
];

// ── Vehicle ─────────────────────────────────────────────────

const VEHICLE_TYPES: DocumentTypeEntry[] = [
  { type: 'registration',   label: 'Registration',    color: 'bg-blue-100 text-blue-700', description: 'Vehicle registration certificate' },
  { type: 'title',          label: 'Title',           color: 'bg-blue-100 text-blue-700', description: 'Certificate of title' },
  { type: 'insurance_card', label: 'Insurance Card',  color: 'bg-blue-100 text-blue-700', description: 'Auto insurance card' },
  { type: 'service_record', label: 'Service Record',  color: 'bg-blue-100 text-blue-700', description: 'Maintenance or repair record' },
  { type: 'inspection',     label: 'Inspection',      color: 'bg-blue-100 text-blue-700', description: 'Safety or emissions inspection' },
  { type: 'loan',           label: 'Loan Documents',  color: 'bg-blue-100 text-blue-700', description: 'Auto loan or financing agreement' },
  { type: 'warranty',       label: 'Warranty',        color: 'bg-blue-100 text-blue-700', description: 'Warranty coverage details' },
  { type: 'other',          label: 'Other',           color: 'bg-blue-100 text-blue-700', description: 'Other document' },
];

// ── Insurance ───────────────────────────────────────────────

const INSURANCE_TYPES: DocumentTypeEntry[] = [
  { type: 'policy',         label: 'Policy Document',          color: 'bg-blue-100 text-blue-700', description: 'Full policy document' },
  { type: 'card',           label: 'Insurance Card',           color: 'bg-blue-100 text-blue-700', description: 'Insurance ID card' },
  { type: 'claim',          label: 'Claim',                    color: 'bg-blue-100 text-blue-700', description: 'Filed claim or claim summary' },
  { type: 'receipt',        label: 'Receipt',                  color: 'bg-blue-100 text-blue-700', description: 'Payment receipt' },
  { type: 'correspondence', label: 'Correspondence',           color: 'bg-blue-100 text-blue-700', description: 'Letters or notices from insurer' },
  { type: 'eob',            label: 'Explanation of Benefits',  color: 'bg-blue-100 text-blue-700', description: 'EOB from insurer' },
  { type: 'declaration',    label: 'Declaration',              color: 'bg-blue-100 text-blue-700', description: 'Declarations page' },
  { type: 'renewal',        label: 'Renewal Notice',           color: 'bg-blue-100 text-blue-700', description: 'Policy renewal notice' },
  { type: 'other',          label: 'Other',                    color: 'bg-blue-100 text-blue-700', description: 'Other document' },
];

// ── Health ──────────────────────────────────────────────────

const HEALTH_TYPES: DocumentTypeEntry[] = [
  { type: 'insurance_card',  label: 'Insurance Card',   color: 'bg-blue-100 text-blue-700', description: 'Health insurance ID card' },
  { type: 'prescription',    label: 'Prescription',     color: 'bg-blue-100 text-blue-700', description: 'Prescription or medication order' },
  { type: 'lab_result',      label: 'Lab Result',       color: 'bg-blue-100 text-blue-700', description: 'Laboratory test results' },
  { type: 'medical_record',  label: 'Medical Record',   color: 'bg-blue-100 text-blue-700', description: 'Medical records or visit summary' },
  { type: 'immunization',    label: 'Immunization',     color: 'bg-blue-100 text-blue-700', description: 'Vaccination or immunization record' },
  { type: 'invoice',         label: 'Invoice / Bill',   color: 'bg-blue-100 text-blue-700', description: 'Medical bill or invoice' },
  { type: 'referral',        label: 'Referral',         color: 'bg-blue-100 text-blue-700', description: 'Provider referral letter' },
  { type: 'other',           label: 'Other',            color: 'bg-blue-100 text-blue-700', description: 'Other document' },
];

// ── Financial ───────────────────────────────────────────────

const FINANCIAL_TYPES: DocumentTypeEntry[] = [
  { type: 'statement',         label: 'Statement',         color: 'bg-blue-100 text-blue-700', description: 'Account or bank statement' },
  { type: 'tax_document',      label: 'Tax Document',      color: 'bg-blue-100 text-blue-700', description: 'Tax form or filing' },
  { type: 'loan_agreement',    label: 'Loan Agreement',    color: 'bg-blue-100 text-blue-700', description: 'Loan or financing agreement' },
  { type: 'card_agreement',    label: 'Card Agreement',    color: 'bg-blue-100 text-blue-700', description: 'Credit card terms or agreement' },
  { type: 'investment_report', label: 'Investment Report', color: 'bg-blue-100 text-blue-700', description: 'Portfolio or investment report' },
  { type: 'receipt',           label: 'Receipt',           color: 'bg-blue-100 text-blue-700', description: 'Payment receipt' },
  { type: 'other',             label: 'Other',             color: 'bg-blue-100 text-blue-700', description: 'Other document' },
];

// ── Personal (future-ready) ─────────────────────────────────

const PERSONAL_TYPES: DocumentTypeEntry[] = [
  { type: 'drivers_license',      label: "Driver's License",       color: 'bg-blue-100 text-blue-700', description: 'State-issued driver\'s license' },
  { type: 'passport',             label: 'Passport',               color: 'bg-blue-100 text-blue-700', description: 'Passport or travel document' },
  { type: 'birth_certificate',    label: 'Birth Certificate',      color: 'bg-blue-100 text-blue-700', description: 'Certified birth certificate' },
  { type: 'social_security',      label: 'Social Security Card',   color: 'bg-blue-100 text-blue-700', description: 'Social Security card' },
  { type: 'marriage_certificate', label: 'Marriage Certificate',    color: 'bg-blue-100 text-blue-700', description: 'Marriage or domestic partnership certificate' },
  { type: 'immigration',          label: 'Immigration Document',    color: 'bg-blue-100 text-blue-700', description: 'Green card, visa, or immigration papers' },
  { type: 'tax_return',           label: 'Tax Return',             color: 'bg-blue-100 text-blue-700', description: 'Personal tax return filing' },
  { type: 'other',                label: 'Other',                  color: 'bg-blue-100 text-blue-700', description: 'Other document' },
];

// ── Utility (child of Address) ───────────────────────────────

const UTILITY_TYPES: DocumentTypeEntry[] = [
  { type: 'bill',        label: 'Bill',              color: 'bg-blue-100 text-blue-700', description: 'Monthly or periodic bill' },
  { type: 'statement',   label: 'Statement',         color: 'bg-blue-100 text-blue-700', description: 'Account statement' },
  { type: 'agreement',   label: 'Service Agreement', color: 'bg-blue-100 text-blue-700', description: 'Service or contract agreement' },
  { type: 'receipt',     label: 'Receipt',            color: 'bg-blue-100 text-blue-700', description: 'Payment receipt' },
  { type: 'notice',      label: 'Notice',             color: 'bg-blue-100 text-blue-700', description: 'Service notice or alert' },
  { type: 'other',       label: 'Other',              color: 'bg-blue-100 text-blue-700', description: 'Other document' },
];

// ── Service Provider (child of Address) ──────────────────────

const SERVICE_PROVIDER_TYPES: DocumentTypeEntry[] = [
  { type: 'invoice',     label: 'Invoice',  color: 'bg-blue-100 text-blue-700', description: 'Service invoice' },
  { type: 'estimate',    label: 'Estimate', color: 'bg-blue-100 text-blue-700', description: 'Cost estimate or quote' },
  { type: 'contract',    label: 'Contract', color: 'bg-blue-100 text-blue-700', description: 'Service contract or agreement' },
  { type: 'receipt',     label: 'Receipt',  color: 'bg-blue-100 text-blue-700', description: 'Payment receipt' },
  { type: 'warranty',    label: 'Warranty', color: 'bg-blue-100 text-blue-700', description: 'Warranty or guarantee' },
  { type: 'report',      label: 'Report',   color: 'bg-blue-100 text-blue-700', description: 'Inspection or service report' },
  { type: 'other',       label: 'Other',    color: 'bg-blue-100 text-blue-700', description: 'Other document' },
];

// ── Master registry ─────────────────────────────────────────

const REGISTRY: Record<DocumentEntityType, DocumentTypeEntry[]> = {
  address: ADDRESS_TYPES,
  vehicle: VEHICLE_TYPES,
  insurance: INSURANCE_TYPES,
  health: HEALTH_TYPES,
  financial: FINANCIAL_TYPES,
  personal: PERSONAL_TYPES,
  utility: UTILITY_TYPES,
  'service-provider': SERVICE_PROVIDER_TYPES,
};

// ── Filter helpers ──────────────────────────────────────────

const HOME_WORK_TYPES: Set<Address['type']> = new Set(['home', 'work']);

interface FilterOptions {
  ownership?: AddressOwnership;
  addressType?: Address['type'];
}

function filterEntries(entries: DocumentTypeEntry[], opts?: FilterOptions): DocumentTypeEntry[] {
  let result = entries;
  if (opts?.ownership) {
    result = result.filter(e => !e.appliesTo || e.appliesTo.includes(opts.ownership!));
  }
  if (opts?.addressType && !HOME_WORK_TYPES.has(opts.addressType)) {
    result = result.filter(e => !e.homeOnly);
  }
  return result;
}

// ── Accessor functions (dropdown consumers) ─────────────────

/** Label map: { type → label } */
export function getDocTypeLabels(entity: DocumentEntityType, ownership?: AddressOwnership): Record<string, string> {
  return Object.fromEntries(filterEntries(REGISTRY[entity], { ownership }).map(e => [e.type, e.label]));
}

/** Color map: { type → Tailwind badge classes } */
export function getDocTypeColors(entity: DocumentEntityType, ownership?: AddressOwnership): Record<string, string> {
  return Object.fromEntries(filterEntries(REGISTRY[entity], { ownership }).map(e => [e.type, e.color]));
}

/** Ordered type keys: ['registration', 'title', ...] */
export function getDocTypeOptions(entity: DocumentEntityType, ownership?: AddressOwnership): string[] {
  return filterEntries(REGISTRY[entity], { ownership }).map(e => e.type);
}

/** Picker-ready options: [{ value: 'registration', label: 'Registration' }, ...] */
export function getDocTypePickerOptions(entity: DocumentEntityType, ownership?: AddressOwnership, addressType?: Address['type']): { value: string; label: string }[] {
  return filterEntries(REGISTRY[entity], { ownership, addressType }).map(e => ({ value: e.type, label: e.label }));
}

/** Full entry array (for advanced consumers) */
export function getDocTypeEntries(entity: DocumentEntityType, ownership?: AddressOwnership): DocumentTypeEntry[] {
  return filterEntries(REGISTRY[entity], { ownership });
}

